import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus } from 'lucide-react';
import { AsyncSelect } from 'chakra-react-select';
import { useMemo, useState, useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { FormContentContainer } from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { ProviderCauseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseStatus.schema';
import { ProviderPointsAllocationKindSchema } from '@duke-hq/svc-api-db/schemas/enums/ProviderPointsAllocationKind.schema';
import { ContributionSection } from './ContributionSection';

type UserProps = {
  drn: string;
  firstName: string;
  lastName: string;
  pictureUrl: string | null;
  totalPoints: number;
};

type Props = {
  user: UserProps;
};

type AssignPointsFormProps = {
  user: UserProps;
  onClose: () => void;
};

type SelectOption = {
  label: string;
  value: string;
};

function AssignPointsForm({ user, onClose }: AssignPointsFormProps) {
  const [cause, setCause] = useState<string | undefined>(undefined);
  const [causeError, setCauseError] = useState<string>('');

  const toast = useToast();
  const utils = trpcTenant.useUtils();

  const { data: causes, isPending: isCausesPending } = trpcTenant.groupRewards.listCauses.useQuery({
    filters: {
      status: ProviderCauseStatusSchema.enum.APPROVED,
    },
  });

  const allocateUserPoints = trpcTenant.groupRewards.allocateUserPoints.useMutation({
    onSuccess: () => {
      toast({
        title: t`Success`,
        description: t`Points allocated successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: error?.message ?? t`Failed to allocate points.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    async onSettled() {
      setCauseError('');
      setCause(undefined);
      await Promise.all([
        utils.groupRewards.listContributorsLeaderboardWithInfiniteScroll.refetch(),
        utils.groupRewards.listSiteLeaderboardWithInfiniteScroll.refetch(),
      ]);
    },
  });

  const causesOptions = useMemo(() => {
    if (!causes) {
      return [];
    }
    return causes
      .filter((cause) => typeof cause.name === 'string' && typeof cause.id === 'string')
      .map((cause) => ({
        label: cause.name,
        value: cause.id,
      }));
  }, [causes]);

  const loadCausesOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (causesOptions.length > 0) {
      const filteredOptions = causesOptions.filter((option) => {
        const label = option.label || '';
        const value = option.value || '';
        return (
          label.toLowerCase().includes(inputValue.toLowerCase()) ||
          value.toLowerCase().includes(inputValue.toLowerCase())
        );
      });
      callback(filteredOptions);
    } else {
      callback([]);
    }
  }, 500);

  const handleContribute = useCallback(
    async (amount: number) => {
      if (cause == null) {
        setCauseError(t`Please select a cause before assigning points`);
        return;
      }

      await allocateUserPoints.mutateAsync({
        causeId: cause,
        pointsAllocated: amount,
        allocatedByDrn: user.drn,
        allocationType: ProviderPointsAllocationKindSchema.enum.ADMIN_ALLOCATION,
      });
    },
    [allocateUserPoints, cause, user.drn],
  );

  return (
    <Box>
      <FormContentContainer>
        <VStack spacing={4} align="stretch" width="100%" my={4}>
          <HStack
            w="full"
            borderRadius="6px"
            border="1px solid"
            borderColor="border.emphasis"
            padding="8px 12px"
            gap="12px"
            justify="space-between"
          >
            <HStack spacing={3}>
              <UserAvatar
                user={{
                  first_name: user?.firstName != null ? user.firstName : '',
                  last_name: user?.lastName != null ? user.lastName : '',
                  picture_url: user?.pictureUrl != null ? user.pictureUrl : null,
                }}
                width="32px"
                height="32px"
                boxSize="4"
                fontSize="xs"
              />
              <HStack spacing={1}>
                <Text fontSize="sm" fontWeight="semibold" as="span">
                  {[user.firstName ?? '', user.lastName ?? ''].filter(Boolean).join(' ')}{' '}
                </Text>
                <Text fontSize="sm" as="span" color="fg.subtle">
                  <Trans>(Deactivated)</Trans>
                </Text>
              </HStack>
            </HStack>
            <Box>
              <Text fontSize="sm" color="fg.subtle" fontWeight="bold">
                {user.totalPoints} {user.totalPoints === 1 ? t`Point` : t`Points`}
              </Text>
            </Box>
          </HStack>

          <Box width="100%">
            <FormControl isRequired isInvalid={!!causeError}>
              <FormLabel htmlFor="cause" mb={2}>
                <Trans>Cause</Trans>
              </FormLabel>

              <AsyncSelect
                id="cause"
                name="cause"
                placeholder={t`Please select a cause`}
                defaultOptions={causesOptions}
                loadOptions={loadCausesOptions}
                selectedOptionColorScheme={'brand'}
                onChange={(val: SelectOption | null) => {
                  setCause(val?.value);
                  if (val?.value != null) {
                    setCauseError('');
                  }
                }}
                isLoading={isCausesPending}
                isDisabled={causesOptions.length === 0}
                chakraStyles={{
                  container: (provided: Record<string, unknown>) => ({
                    ...provided,
                    width: '100%',
                  }),
                }}
              />

              {causeError ? <FormErrorMessage>{causeError}</FormErrorMessage> : null}
            </FormControl>
          </Box>

          <ContributionSection
            pointsAvailable={user.totalPoints}
            onContribute={handleContribute}
            buttonText="Assign points"
            buttonSize="md"
            showPointsAvailable={false}
            width="full"
            margin={0}
            isDisabled={cause == null}
          />
        </VStack>
      </FormContentContainer>
    </Box>
  );
}

export function AssignPointsModal({ user }: Props): React.ReactElement {
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <FormModalWrapper
      modalOpenButtonText={t`Assign points`}
      modalOpenButtonLeftIcon={Plus}
      modalOpenButtonVariant="secondary"
      modalContent={<AssignPointsForm user={user} onClose={onClose} />}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      modalContentHeight="auto"
      modalOpenButtonDisabled={user.totalPoints <= 0}
    />
  );
}
